import { useState } from 'react'
import { toast } from 'sonner'
import {
  FormTemplate, TemplateSection, TemplateField,
  apiCreateTemplate, apiUpdateTemplate, apiListCampaigns, UpdateTemplatePayload,
} from '../lib/api'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Checkbox } from './ui/checkbox'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select'
import { GripVertical, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react'

// ─── Types locaux ─────────────────────────────────────────────────────────────

export type FieldDraft = {
  id: string
  label: string
  type: 'text' | 'number' | 'select' | 'file' | 'date'
  unit: string
  description: string
  required: boolean
  optionsRaw: string // CSV pour le type select, ex: "Option1, Option2"
  // Story 6.4 — options d'origine, réutilisées telles quelles si `optionsRaw`
  // n'a pas été modifié (une option contenant une virgule survit au round-trip).
  originalOptions?: { raw: string; options: string[] }
}

export type SectionDraft = {
  id: string
  title: string
  order: number
  fields: FieldDraft[]
}

// ─── Props ────────────────────────────────────────────────────────────────────

type TemplateEditorProps = {
  template?: FormTemplate | null  // null/undefined = création, sinon édition
  onSave: () => void              // appelé après sauvegarde réussie (recharge la liste)
  onCancel: () => void
}

// ─── Utilitaire : transformer FormTemplate → SectionDraft[] ──────────────────

// Exporté (story 6.4) pour le test de round-trip des templates IA.
export function templateToSections(template: FormTemplate | null | undefined): SectionDraft[] {
  if (!template || !Array.isArray(template.sections)) return []
  return (template.sections as TemplateSection[]).map((s) => ({
    id: s.id,
    title: s.title,
    order: s.order,
    fields: s.fields.map((f: TemplateField) => {
      const optionsRaw = f.options ? f.options.join(', ') : ''
      return {
        id: f.id,
        label: f.label,
        type: f.type,
        unit: f.unit ?? '',
        description: f.description ?? '',
        required: f.required,
        optionsRaw,
        ...(f.options ? { originalOptions: { raw: optionsRaw, options: f.options } } : {}),
      }
    }),
  }))
}

function selectOptions(f: FieldDraft): string[] {
  if (f.originalOptions && f.originalOptions.raw === f.optionsRaw) return f.originalOptions.options
  return f.optionsRaw
    .split(',')
    .map((o) => o.trim())
    .filter(Boolean)
}

// Payload envoyé à l'API (création / PATCH). Exporté (story 6.4) pour le test de round-trip.
export function sectionsToPayload(name: string, description: string, sections: SectionDraft[]) {
  return {
    name: name.trim(),
    description: description.trim() || undefined,
    sections: sections.map((s) => ({
      id: s.id,
      title: s.title.trim() || 'Section sans titre',
      order: s.order,
      fields: s.fields.map((f) => ({
        id: f.id,
        label: f.label.trim(),
        type: f.type,
        unit: f.unit.trim() || undefined,
        description: f.description.trim() || undefined,
        required: f.required,
        options: f.type === 'select' ? selectOptions(f) : undefined,
      })),
    })),
  }
}

// ─── Composant principal ─────────────────────────────────────────────────────

export function TemplateEditor({ template, onSave, onCancel }: TemplateEditorProps) {
  const isEditing = !!template
  const [name, setName] = useState(template?.name ?? '')
  const [description, setDescription] = useState(template?.description ?? '')
  const [sections, setSections] = useState<SectionDraft[]>(() => templateToSections(template))
  const [nameError, setNameError] = useState('')
  const [submitErrors, setSubmitErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null)
  const [showActiveCampaignWarning, setShowActiveCampaignWarning] = useState(false)
  const [pendingSavePayload, setPendingSavePayload] = useState<UpdateTemplatePayload | null>(null)

  // ─── Mutations de l'état ───────────────────────────────────────────────────

  const newId = () => crypto.randomUUID()

  const addSection = () => {
    setSections((prev) => [
      ...prev,
      { id: newId(), title: 'Nouvelle section', order: prev.length, fields: [] },
    ])
  }

  const removeSection = (sectionId: string) => {
    setSections((prev) =>
      prev.filter((s) => s.id !== sectionId).map((s, i) => ({ ...s, order: i }))
    )
  }

  const updateSectionTitle = (sectionId: string, title: string) => {
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, title } : s)))
  }

  const addField = (sectionId: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id !== sectionId
          ? s
          : {
              ...s,
              fields: [
                ...s.fields,
                {
                  id: newId(),
                  label: '',
                  type: 'text' as const,
                  unit: '',
                  description: '',
                  required: false,
                  optionsRaw: '',
                },
              ],
            }
      )
    )
  }

  const removeField = (sectionId: string, fieldId: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id !== sectionId ? s : { ...s, fields: s.fields.filter((f) => f.id !== fieldId) }
      )
    )
  }

  const updateField = (sectionId: string, fieldId: string, patch: Partial<FieldDraft>) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id !== sectionId
          ? s
          : {
              ...s,
              fields: s.fields.map((f) => (f.id !== fieldId ? f : { ...f, ...patch })),
            }
      )
    )
  }

  // ─── Drag-and-drop HTML5 ───────────────────────────────────────────────────

  const handleDragStart = (idx: number) => setDraggedIdx(idx)

  const handleDragOver = (e: React.DragEvent) => e.preventDefault()

  const handleDrop = (targetIdx: number) => {
    if (draggedIdx === null || draggedIdx === targetIdx) return
    const next = [...sections]
    const [removed] = next.splice(draggedIdx, 1)
    next.splice(targetIdx, 0, removed)
    setSections(next.map((s, i) => ({ ...s, order: i })))
    setDraggedIdx(null)
  }

  const handleDragEnd = () => setDraggedIdx(null)

  // ─── Validation et soumission ──────────────────────────────────────────────

  const handleNameBlur = () => {
    setNameError(name.trim() === '' ? 'Le titre du template est requis' : '')
  }

  const validate = (): string[] => {
    const errors: string[] = []
    if (name.trim() === '') errors.push('Le titre du template est requis')
    if (sections.length === 0) errors.push('Ajoutez au moins une section')
    sections.forEach((s) => {
      if (s.fields.length === 0) {
        errors.push(`La section "${s.title || 'Sans titre'}" ne contient aucun champ`)
      }
      s.fields.forEach((f) => {
        if (f.label.trim() === '') {
          errors.push(`Un champ de la section "${s.title}" n'a pas de label`)
        }
      })
    })
    return errors
  }

  const buildPayload = () => sectionsToPayload(name, description, sections)

  const handleSubmit = async () => {
    const errors = validate()
    if (errors.length > 0) {
      setSubmitErrors(errors)
      if (name.trim() === '') setNameError('Le titre du template est requis')
      return
    }
    setSubmitErrors([])
    setSubmitting(true)
    try {
      const payload = buildPayload()
      if (isEditing && template) {
        // CA-5 : vérifier si le template est utilisé dans des campagnes ACTIVE
        const activeCampaigns = await apiListCampaigns({ formTemplateId: template.id, status: 'ACTIVE' })
        if (activeCampaigns.length > 0) {
          setPendingSavePayload(payload)
          setShowActiveCampaignWarning(true)
          setSubmitting(false)
          return
        }
        await apiUpdateTemplate(template.id, payload)
        toast.success('Template mis à jour', { duration: 4000 })
      } else {
        await apiCreateTemplate(payload)
        toast.success('Template créé', { duration: 4000 })
      }
      onSave()
    } catch (e: unknown) {
      toast.error('Erreur lors de la sauvegarde du template', { duration: Infinity })
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfirmSave = async () => {
    if (!template || !pendingSavePayload) return
    setShowActiveCampaignWarning(false)
    setSubmitting(true)
    try {
      await apiUpdateTemplate(template.id, pendingSavePayload)
      toast.success('Template mis à jour', { duration: 4000 })
      setPendingSavePayload(null)
      onSave()
    } catch (e: unknown) {
      toast.error('Erreur lors de la sauvegarde du template', { duration: Infinity })
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Rendu ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">
          {isEditing ? 'Modifier le template' : 'Nouveau template'}
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      {/* Erreurs globales (section vide, etc.) */}
      {submitErrors.length > 0 && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4 space-y-1">
          {submitErrors.map((err, i) => (
            <p key={i} className="text-sm text-red-700">
              {err}
            </p>
          ))}
        </div>
      )}

      {/* Titre du template */}
      <div className="space-y-1.5">
        <Label htmlFor="template-name">
          Titre du template <span className="text-red-500">*</span>
        </Label>
        <Input
          id="template-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value)
            if (nameError) setNameError('')
          }}
          onBlur={handleNameBlur}
          placeholder="Ex : Collecte données carbone fournisseurs 2026"
        />
        {nameError && <p className="text-sm text-red-600">{nameError}</p>}
      </div>

      {/* Description (optionnelle) */}
      <div className="space-y-1.5">
        <Label htmlFor="template-description">Description (optionnelle)</Label>
        <Input
          id="template-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Décrivez l'objectif de ce template..."
        />
      </div>

      {/* Sections */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Sections</h3>

        {sections.length === 0 && (
          <p className="text-sm text-muted-foreground italic">
            Ajoutez au moins une section pour commencer.
          </p>
        )}

        {sections.map((section, sIdx) => (
          <div
            key={section.id}
            draggable
            onDragStart={() => handleDragStart(sIdx)}
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(sIdx)}
            onDragEnd={handleDragEnd}
            className={`rounded-lg border bg-white p-4 space-y-4 transition-opacity ${
              draggedIdx === sIdx ? 'opacity-50' : 'opacity-100'
            }`}
          >
            {/* En-tête section */}
            <div className="flex items-center gap-2">
              <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab shrink-0" />
              <Input
                value={section.title}
                onChange={(e) => updateSectionTitle(section.id, e.target.value)}
                placeholder="Titre de la section"
                className="font-medium"
              />
              <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-700 shrink-0"
                onClick={() => removeSection(section.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {/* Champs de la section */}
            <div className="space-y-3 pl-6">
              {section.fields.map((field) => (
                <FieldEditor
                  key={field.id}
                  field={field}
                  onChange={(patch) => updateField(section.id, field.id, patch)}
                  onRemove={() => removeField(section.id, field.id)}
                />
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => addField(section.id)}
                className="w-full border-dashed"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Ajouter un champ
              </Button>
            </div>
          </div>
        ))}

        <Button variant="outline" onClick={addSection} className="w-full border-dashed">
          <Plus className="h-4 w-4 mr-1.5" />
          Ajouter une section
        </Button>
      </div>

      {/* Dialog avertissement CA-5 : template utilisé dans des campagnes actives */}
      <Dialog open={showActiveCampaignWarning} onOpenChange={setShowActiveCampaignWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Template utilisé dans des campagnes actives</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Ce template est utilisé dans des campagnes actives. Les modifications n'affecteront pas les formulaires déjà envoyés aux fournisseurs.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowActiveCampaignWarning(false)
                setPendingSavePayload(null)
              }}
            >
              Annuler
            </Button>
            <Button onClick={handleConfirmSave} disabled={submitting}>
              {submitting ? 'Enregistrement...' : 'Confirmer et sauvegarder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Sous-composant FieldEditor (interne, non exporté) ────────────────────────

type FieldEditorProps = {
  field: FieldDraft
  onChange: (patch: Partial<FieldDraft>) => void
  onRemove: () => void
}

function FieldEditor({ field, onChange, onRemove }: FieldEditorProps) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="rounded-md border bg-gray-50 p-3 space-y-3">
      <div className="flex items-center gap-2">
        {/* Label (requis) */}
        <Input
          value={field.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Label du champ *"
          className="flex-1 bg-white"
        />
        {/* Type */}
        <Select
          value={field.type}
          onValueChange={(v) => onChange({ type: v as FieldDraft['type'] })}
        >
          <SelectTrigger className="w-32 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="text">Texte</SelectItem>
            <SelectItem value="number">Nombre</SelectItem>
            <SelectItem value="select">Liste</SelectItem>
            <SelectItem value="file">Fichier</SelectItem>
            <SelectItem value="date">Date</SelectItem>
          </SelectContent>
        </Select>
        {/* Requis */}
        <div className="flex items-center gap-1.5 shrink-0">
          <Checkbox
            id={`req-${field.id}`}
            checked={field.required}
            onCheckedChange={(v) => onChange({ required: !!v })}
          />
          <Label htmlFor={`req-${field.id}`} className="text-xs whitespace-nowrap">
            Obligatoire
          </Label>
        </div>
        {/* Expand/collapse options avancées */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="shrink-0"
          title="Options avancées"
        >
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </Button>
        {/* Supprimer champ */}
        <Button
          variant="ghost"
          size="sm"
          className="text-red-400 hover:text-red-600 shrink-0"
          onClick={onRemove}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Options avancées (unité, description, options select) */}
      {expanded && (
        <div className="space-y-2 pt-1">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground">Unité de mesure</Label>
              <Input
                value={field.unit}
                onChange={(e) => onChange({ unit: e.target.value })}
                placeholder="Ex : kWh, kg CO₂eq, %"
                className="bg-white text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Description contextuelle</Label>
              <Input
                value={field.description}
                onChange={(e) => onChange({ description: e.target.value })}
                placeholder="Aide affichée au fournisseur..."
                className="bg-white text-sm"
              />
            </div>
          </div>
          {field.type === 'select' && (
            <div>
              <Label className="text-xs text-muted-foreground">
                Options (séparées par des virgules)
              </Label>
              <Input
                value={field.optionsRaw}
                onChange={(e) => onChange({ optionsRaw: e.target.value })}
                placeholder="Ex : Option 1, Option 2, Option 3"
                className="bg-white text-sm"
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
